import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthContext.js";
import { ApiError } from "../lib/api.js";
import type { TotpSetupResult } from "../auth/types.js";
import { AUTH_CARD_CLASS, AUTH_INPUT_CLASS, AuthShell } from "../components/AuthShell.js";
import { Button } from "../components/Button.js";

export const TotpEnrolPage = ({
  challengeId,
  onBack,
}: {
  challengeId: string;
  onBack: () => void;
}): JSX.Element => {
  const { setupTotp, confirmTotp } = useAuth();
  const [setup, setSetup] = useState<TotpSetupResult | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setupTotp(challengeId)
      .then((result) => {
        if (!cancelled) setSetup(result);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof ApiError ? err.message : "Could not start enrolment.");
      });
    return () => {
      cancelled = true;
    };
  }, [challengeId, setupTotp]);

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await confirmTotp(challengeId, code);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <div className={AUTH_CARD_CLASS}>
        <div>
          <h1 className="font-heading text-2xl font-semibold">Set up two-factor authentication</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Required for every staff account. Scan this code with an authenticator app (Google
            Authenticator, 1Password, Authy).
          </p>
        </div>

        {!setup && !error && (
          <p className="text-sm text-muted-foreground">Preparing your QR code…</p>
        )}

        {setup && (
          <>
            <img
              src={setup.qrCodeDataUrl}
              alt="Scan with your authenticator app"
              className="mx-auto h-48 w-48 rounded-md border p-2"
            />
            <details className="text-sm text-muted-foreground">
              <summary className="cursor-pointer select-none">
                Can't scan? Enter this key manually
              </summary>
              <code className="mt-2 block break-all rounded bg-muted p-2 text-xs">
                {setup.secret}
              </code>
            </details>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="enrol-code" className="text-sm font-medium">
                  Enter the code from your app to confirm
                </label>
                <input
                  id="enrol-code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className={`${AUTH_INPUT_CLASS} text-center text-lg tracking-[0.5em]`}
                />
              </div>

              {error && (
                <p
                  role="alert"
                  className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {error}
                </p>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={submitting || code.length !== 6}
                className="w-full"
              >
                {submitting ? "Confirming…" : "Confirm and finish setup"}
              </Button>
            </form>
          </>
        )}

        {error && !setup && (
          <p
            role="alert"
            className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={onBack}
          className="w-full rounded-md py-2 text-center text-sm text-muted-foreground underline hover:text-foreground"
        >
          Back to sign in
        </button>
      </div>
    </AuthShell>
  );
};
